import { formatDouble } from './numbersUtils';

export type CartLocation = {
  id?: number | string;
  name?: string;
  city?: string;
  state?: string;
};

export type CartLocationGroup = {
  locationKey: string;
  location: CartLocation | null;
  locationName: string;
  items: any[];
};

export type RentalCheckResult = {
  success: boolean;
  error: { message: string };
};

export function getLocationDisplayName(location?: CartLocation | null): string {
  if (!location) return 'Unknown location';
  return (
    location.name ||
    [location.city, location.state].filter(Boolean).join(', ') ||
    'Unknown location'
  );
}

export function getLocationKey(item: { location?: CartLocation | null }): string {
  const id = item?.location?.id;
  if (id !== undefined && id !== null && id !== '') {
    return String(id);
  }
  return getLocationDisplayName(item?.location);
}

export function hasCartItemsAtLocation(
  cartItems: any[],
  item: { location?: CartLocation | null },
): boolean {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return false;
  const locationKey = getLocationKey(item);
  return cartItems.some((cartItem) => getLocationKey(cartItem) === locationKey);
}

export function getRemainingCartItemsAfterCheckout(
  allCartItems: any[],
  checkoutItems: any[],
): any[] {
  if (!Array.isArray(allCartItems) || !allCartItems.length) return [];
  if (!Array.isArray(checkoutItems) || !checkoutItems.length) return allCartItems;

  const checkoutAssetIds = new Set(
    checkoutItems.map((item) => String(item?.assetId)).filter(Boolean),
  );

  return allCartItems.filter(
    (item) => !checkoutAssetIds.has(String(item?.assetId)),
  );
}

export function groupCartItemsByLocation(items: any[]): CartLocationGroup[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  const groups = new Map<string, CartLocationGroup>();

  for (const item of items) {
    const locationKey = getLocationKey(item);
    const existing = groups.get(locationKey);

    if (existing) {
      existing.items.push(item);
      continue;
    }

    groups.set(locationKey, {
      locationKey,
      location: item?.location ?? null,
      locationName: getLocationDisplayName(item?.location),
      items: [item],
    });
  }

  return Array.from(groups.values());
}

export function getItemsForLocation(
  items: any[],
  locationKey?: string | number | null,
): any[] {
  if (!locationKey) return items;
  return items.filter((item) => getLocationKey(item) === String(locationKey));
}

export function hasMultipleCartLocations(cartItems: any[]): boolean {
  if (!Array.isArray(cartItems) || cartItems.length <= 1) return false;
  const locationKeys = new Set(cartItems.map((item) => getLocationKey(item)));
  return locationKeys.size > 1;
}

export function getCheckoutItems(
  cartItems: any[],
  selectedCheckoutLocationId?: string | null,
): any[] {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return [];
  if (selectedCheckoutLocationId) {
    return getItemsForLocation(cartItems, selectedCheckoutLocationId);
  }
  if (hasMultipleCartLocations(cartItems)) {
    return [];
  }
  return cartItems;
}

/** One asset per location — the item that will be booked and shown at checkout. */
export function getSingleCheckoutItem(
  cartItems: any[],
  selectedCheckoutLocationId?: string | null,
): any | null {
  const items = getCheckoutItems(cartItems, selectedCheckoutLocationId);
  return items[0] ?? null;
}

export function hasMultipleItemsAtCheckoutLocation(
  cartItems: any[],
  selectedCheckoutLocationId?: string | null,
): boolean {
  return getCheckoutItems(cartItems, selectedCheckoutLocationId).length > 1;
}

export function dedupeCartItemsByLocation(items: any[]): any[] {
  if (!Array.isArray(items) || items.length === 0) return [];

  const byLocation = new Map<string, any>();
  for (const item of items) {
    const locationKey = getLocationKey(item);
    if (!byLocation.has(locationKey)) {
      byLocation.set(locationKey, item);
    }
  }

  return Array.from(byLocation.values());
}

export function resolveSelectedCheckoutLocationId(
  cartItems: any[],
  selectedCheckoutLocationId?: string | null,
): string | null {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return null;

  if (selectedCheckoutLocationId) {
    const matchingItems = getItemsForLocation(cartItems, selectedCheckoutLocationId);
    if (matchingItems.length > 0) return String(selectedCheckoutLocationId);
  }

  return getLocationKey(cartItems[0]);
}

export function hasStoredCartAssetIds(): boolean {
  try {
    const ids = JSON.parse(localStorage.getItem('CartAssetIds') || '[]');
    return Array.isArray(ids) && ids.length > 0;
  } catch {
    return false;
  }
}

export function syncSelectedAddonIdsToStorage(
  selectedAddonServiceIdsByAssetId: Record<string, number[]>,
) {
  try {
    localStorage.setItem(
      'CartSelectedAddonIds',
      JSON.stringify(selectedAddonServiceIdsByAssetId || {}),
    );
  } catch {}
}

export function syncSelectedAddonIdsForCartItems(
  cartItems: any[],
  selectedAddonServiceIdsByAssetId: Record<string, number[]>,
) {
  const assetIds = new Set(
    cartItems.map((item) => String(item?.assetId)).filter(Boolean),
  );

  const pruned = Object.entries(selectedAddonServiceIdsByAssetId || {}).reduce<
    Record<string, number[]>
  >((acc, [assetId, ids]) => {
    if (!assetIds.has(assetId) || !Array.isArray(ids) || !ids.length) return acc;
    acc[assetId] = ids;
    return acc;
  }, {});

  syncSelectedAddonIdsToStorage(pruned);
}

export function loadSelectedAddonIdsFromStorage(): Record<string, number[]> {
  try {
    const parsed = JSON.parse(localStorage.getItem('CartSelectedAddonIds') || '{}');
    if (!parsed || typeof parsed !== 'object') return {};

    return Object.entries(parsed).reduce<Record<string, number[]>>((acc, [assetId, ids]) => {
      if (!Array.isArray(ids)) return acc;
      acc[String(assetId)] = ids
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));
      return acc;
    }, {});
  } catch {
    return {};
  }
}

export function getAssetCountLabel(count: number): string {
  return `${count} asset${count === 1 ? '' : 's'}`;
}

export function getEquipFeeForAsset(
  equipFeeByAssetId: Record<string, any> | undefined,
  assetId: string | number | undefined,
  fallbackEquipFee?: any,
): any {
  if (assetId == null || assetId === '') return fallbackEquipFee;
  const fee = equipFeeByAssetId?.[String(assetId)];
  return fee ?? fallbackEquipFee;
}

export function getRentalSubtotal(equipFee: any): number {
  return Number(equipFee?.rentalTotal || 0) + Number(equipFee?.serviceTotal || 0);
}

export function getGrandTotal(equipFee: any): number {
  return Number(equipFee?.grandTotal || 0);
}

export function getHiddenRateMapFees(equipFee: any): number {
  const rateMap = equipFee?.rateMap;
  if (!rateMap) return 0;

  return (
    Number(rateMap.UPGRADE?.fee || 0) +
    Number(rateMap.SURCHARGE?.fee || 0)
  );
}

export function getCartRentalSubtotal(equipFee: any): number {
  if (!equipFee) return 0;

  const hiddenFees = getHiddenRateMapFees(equipFee);
  const subTotal = Number(equipFee.subTotal)+Number(equipFee.discountTotal ||0);

  if (Number.isFinite(subTotal) && subTotal > 0) {
    return Math.max(0, subTotal - hiddenFees);
  }

  return getItemRentalTotal(equipFee);
}

export function getGroupRentalSubtotal(
  items: any[],
  equipFeeByAssetId: Record<string, any> = {},
): number {
  const checkoutItems = Array.isArray(items) && items.length > 1 ? items.slice(0, 1) : items;
  return checkoutItems.reduce((sum, item) => {
    const fee = getEquipFeeForAsset(equipFeeByAssetId, item?.assetId);
    return sum + getCartRentalSubtotal(fee);
  }, 0);
}

export function getItemRentalTotal(equipFee: any): number {
  if (!equipFee?.rentalTotal) return 0;

  return (
    Number(equipFee.rentalTotal) -
    (equipFee?.rateMap?.SURCHARGE?.fee || 0) -
    (equipFee?.rateMap?.UPGRADE?.fee || 0)
  );
}

export function formatCurrency(value: number): string {
  return `$ ${formatDouble(value, 2)}`;
}

export function syncCartAssetIdsToStorage(cartItems: any[]) {
  const ids = cartItems.map((item) => item?.assetId).filter(Boolean);

  if (ids.length) {
    localStorage.setItem('CartAssetIds', JSON.stringify(ids));
    localStorage.setItem(
      'CartAssetIdsMinutes',
      String(Math.round(new Date().getTime() / (1000 * 60))),
    );
    return;
  }

  localStorage.setItem('CartAssetIds', '[]');
  localStorage.setItem('CartAssetIdsMinutes', '0');
  localStorage.removeItem('CartSelectedAddonIds');
}

export function mergeEquipFees(fees: any[]): any {
  const validFees = fees.filter(Boolean);
  if (!validFees.length) return {};
  if (validFees.length === 1) return validFees[0];

  const merged: any = {
    status: 'OK',
    rentalTotal: 0,
    serviceTotal: 0,
    grandTotal: 0,
    securityDeposit: 0,
    services: [],
    promotions: [],
    rateMap: {},
    tax: {},
  };

  for (const fee of validFees) {
    merged.rentalTotal += Number(fee?.rentalTotal || 0);
    merged.serviceTotal += Number(fee?.serviceTotal || 0);
    merged.grandTotal += Number(fee?.grandTotal || 0);
    merged.securityDeposit += Number(fee?.securityDeposit || 0);
    merged.services.push(...(fee?.services || []));
    merged.promotions.push(...(fee?.promotions || []));

    if (fee?.rateMap) {
      for (const [key, entry] of Object.entries(fee.rateMap)) {
        const rateEntry = entry as { rate?: number; count?: number; fee?: number };
        if (!merged.rateMap[key]) {
          merged.rateMap[key] = { ...rateEntry };
          continue;
        }

        merged.rateMap[key] = {
          rate: rateEntry.rate ?? merged.rateMap[key].rate,
          count: Math.max(Number(merged.rateMap[key].count || 0), Number(rateEntry.count || 0)),
          fee: Number(merged.rateMap[key].fee || 0) + Number(rateEntry.fee || 0),
        };
      }
    }

    if (fee?.tax) {
      for (const [key, value] of Object.entries(fee.tax)) {
        if (typeof value === 'number') {
          merged.tax[key] = Number(merged.tax[key] || 0) + value;
        } else if (merged.tax[key] === undefined) {
          merged.tax[key] = value;
        }
      }
    }

    if (fee?.usageIncluded != null && merged.usageIncluded == null) {
      merged.usageIncluded = fee.usageIncluded;
    }
    if (fee?.usageUnlimited != null && merged.usageUnlimited == null) {
      merged.usageUnlimited = fee.usageUnlimited;
    }
    if (fee?.usageType && !merged.usageType) {
      merged.usageType = fee.usageType;
    }
    if (fee?.rentalRateAdjusted) {
      merged.rentalRateAdjusted = true;
    }
  }

  return merged;
}

export function getMergedEquipFeeForItems(
  items: any[],
  equipFeeByAssetId: Record<string, any> = {},
): any {
  const fees = items.map((item) => getEquipFeeForAsset(equipFeeByAssetId, item?.assetId));
  return mergeEquipFees(fees);
}

export function areCheckoutItemsAvailable(items: any[]): boolean {
  if (!items.length) return false;
  return items.every((item) => item?.unavailable !== true);
}
